import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Network,
  ShieldAlert,
  ShieldOff,
  ShieldCheck,
  AlertTriangle,
  Ban,
  CheckCircle2,
  ChevronRight,
  Zap,
  DollarSign,
  Clock,
  Users,
} from "lucide-react";

/* ─── Scenario Definitions ─── */

const scenarios = [
  {
    id: "false-negative",
    tab: "False Negative",
    tabIcon: ShieldOff,
    tabColor: "#EF4444",
    headline: "The Missed Threat",
    headlinePlain: "Sensitive data slips out undetected",
    description:
      "Without taint tracking, a multi-hop data path looks like a series of individually authorized calls. The exfiltration is invisible because no single step is malicious on its own.",
    plainDescription:
      "Imagine an employee asks an AI assistant to \"summarize Q4 revenue and send it to my email.\" Each step looks fine individually — reading data, summarizing, sending email. But the AI sends it to a personal Gmail instead of a work address. Without tracking how sensitive data flows through every step, this looks perfectly normal.",
    impact: {
      icon: DollarSign,
      label: "Average cost of a data breach",
      value: "$4.88M",
      sub: "IBM Cost of Data Breach Report 2024",
    },
    nodes: [
      { id: "agent", label: "AI Agent", x: 50, y: 50, type: "agent" },
      { id: "hr-db", label: "HR Database", x: 200, y: 20, type: "source", classification: "SECRET" },
      { id: "llm", label: "LLM Context", x: 350, y: 50, type: "process" },
      { id: "scratch", label: "Temp Storage", x: 350, y: 130, type: "storage" },
      { id: "slack", label: "Internal Slack", x: 500, y: 20, type: "internal" },
      { id: "email", label: "External Email", x: 530, y: 130, type: "external" },
    ],
    edges: [
      { from: "agent", to: "hr-db", label: "READ salaries", step: 0 },
      { from: "hr-db", to: "llm", label: "Summarize", step: 1 },
      { from: "llm", to: "scratch", label: "Cache result", step: 2 },
      { from: "scratch", to: "slack", label: "Post summary", step: 3, decoy: true },
      { from: "scratch", to: "email", label: "Send copy", step: 4, danger: true },
    ],
    // taint spreads through these edges
    taintPath: [0, 1, 2, 4],
    dangerStep: 4,
    verdict: "MISSED",
    verdictColor: "#EF4444",
    verdictMessage: "Without taint tracking, each call is authorized. The exfiltration succeeds.",
  },
  {
    id: "false-positive",
    tab: "False Positive",
    tabIcon: Ban,
    tabColor: "#F59E0B",
    headline: "The Blocked Workflow",
    headlinePlain: "Legitimate work grinds to a halt",
    description:
      "Overly aggressive rules treat every data-touching workflow as a threat. When the security system cries wolf on routine tasks, teams disable it entirely — leaving you worse off than before.",
    plainDescription:
      "A marketing manager asks the AI to pull last quarter's campaign metrics and email a report to their team lead. A blunt security system sees \"data read + email send\" and blocks it — even though the data isn't sensitive and the recipient is internal. Now multiply this across every employee, every day.",
    impact: {
      icon: Clock,
      label: "Productivity lost to false alarms",
      value: "32%",
      sub: "Of SOC analyst time spent on false positives (Ponemon Institute)",
    },
    nodes: [
      { id: "agent", label: "AI Agent", x: 50, y: 50, type: "agent" },
      { id: "metrics", label: "Campaign Metrics", x: 200, y: 20, type: "source", classification: "INTERNAL" },
      { id: "llm", label: "LLM Context", x: 350, y: 50, type: "process" },
      { id: "report", label: "PDF Generator", x: 350, y: 130, type: "process" },
      { id: "email-int", label: "Team Lead Email", x: 530, y: 50, type: "internal" },
      { id: "drive", label: "Shared Drive", x: 530, y: 130, type: "internal" },
    ],
    edges: [
      { from: "agent", to: "metrics", label: "READ metrics", step: 0 },
      { from: "metrics", to: "llm", label: "Summarize", step: 1 },
      { from: "llm", to: "report", label: "Generate PDF", step: 2 },
      { from: "report", to: "email-int", label: "Email report", step: 3, falseBlock: true },
      { from: "report", to: "drive", label: "Save copy", step: 4, falseBlock: true },
    ],
    taintPath: [0, 1, 2, 3, 4],
    dangerStep: 3,
    verdict: "BLOCKED",
    verdictColor: "#F59E0B",
    verdictMessage: "A blunt rule sees \"data + email\" and blocks everything. Legitimate work stops.",
  },
  {
    id: "leapforce",
    tab: "LeapForce",
    tabIcon: ShieldCheck,
    tabColor: "#00E5FF",
    headline: "Precision Security",
    headlinePlain: "Only real threats get stopped",
    description:
      "Taint tracking follows the actual sensitivity of data through every hop. Low-sensitivity data flows freely. High-sensitivity data triggers enforcement only when it reaches an unauthorized boundary.",
    plainDescription:
      "LeapForce watches the sensitivity level of data at every step — like a colored dye that travels with information. Public metrics stay \"green\" and flow freely. Salary data stays \"red\" no matter how many steps it passes through. The system only intervenes when red data tries to leave the building.",
    impact: {
      icon: Users,
      label: "Enterprise agent adoption with LeapForce",
      value: "10×",
      sub: "Deploy agents confidently, without choosing between security and productivity",
    },
    nodes: [
      { id: "agent", label: "AI Agent", x: 50, y: 80, type: "agent" },
      { id: "hr-db", label: "HR Database", x: 220, y: 20, type: "source", classification: "SECRET" },
      { id: "metrics", label: "Metrics DB", x: 220, y: 140, type: "source", classification: "INTERNAL" },
      { id: "llm", label: "LLM Context", x: 390, y: 80, type: "process" },
      { id: "email-ext", label: "External Email", x: 560, y: 20, type: "external" },
      { id: "slack", label: "Team Slack", x: 560, y: 140, type: "internal" },
    ],
    edges: [
      { from: "agent", to: "hr-db", label: "READ salaries", step: 0, taintLevel: "SECRET" },
      { from: "agent", to: "metrics", label: "READ metrics", step: 1, taintLevel: "INTERNAL" },
      { from: "hr-db", to: "llm", label: "Summarize", step: 2, taintLevel: "SECRET" },
      { from: "metrics", to: "llm", label: "Summarize", step: 3, taintLevel: "INTERNAL" },
      { from: "llm", to: "email-ext", label: "Send report", step: 4, taintLevel: "SECRET", blocked: true },
      { from: "llm", to: "slack", label: "Post metrics", step: 5, taintLevel: "INTERNAL", allowed: true },
    ],
    taintPath: [0, 2, 4],
    dangerStep: 4,
    verdict: "PRECISE",
    verdictColor: "#00E5FF",
    verdictMessage: "SECRET data → external? Blocked. INTERNAL data → internal? Allowed. Zero false positives.",
  },
];

/* ─── Color Helpers ─── */

const classificationColor = (c) => {
  if (c === "SECRET") return "#EF4444";
  if (c === "CONFIDENTIAL") return "#F59E0B";
  return "#10B981";
};

const nodeTypeColor = (type) => {
  if (type === "external") return "#EF4444";
  if (type === "internal") return "#10B981";
  if (type === "source") return "#00E5FF";
  if (type === "process") return "#F59E0B";
  if (type === "storage") return "#94A3B8";
  return "#475569";
};

const nodeTypeBg = (type) => {
  if (type === "external") return "rgba(239, 68, 68, 0.08)";
  if (type === "internal") return "rgba(16, 185, 129, 0.08)";
  if (type === "source") return "rgba(0, 229, 255, 0.08)";
  if (type === "process") return "rgba(245, 158, 11, 0.08)";
  if (type === "storage") return "rgba(148, 163, 184, 0.08)";
  return "rgba(71, 85, 105, 0.08)";
};

/* ─── SVG Graph Components ─── */

function GraphNode({ node, active, tainted, taintColor, pulseDanger, pulseAllow }) {
  const color = nodeTypeColor(node.type);
  const bg = nodeTypeBg(node.type);

  return (
    <motion.g
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{
        opacity: 1,
        scale: 1,
      }}
      transition={{ duration: 0.4, delay: 0.1 }}
    >
      {/* Glow ring for tainted nodes */}
      {tainted && (
        <motion.rect
          x={node.x - 54}
          y={node.y - 22}
          width={108}
          height={44}
          fill="none"
          stroke={taintColor || "#EF4444"}
          strokeWidth={1.5}
          opacity={0.6}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.3, 0.7, 0.3] }}
          transition={{ repeat: Infinity, duration: 2 }}
        />
      )}

      {/* Danger pulse */}
      {pulseDanger && (
        <motion.rect
          x={node.x - 58}
          y={node.y - 26}
          width={116}
          height={52}
          fill="none"
          stroke="#EF4444"
          strokeWidth={2}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.8, 0], scale: [1, 1.1, 1.15] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
        />
      )}

      {/* Allow pulse */}
      {pulseAllow && (
        <motion.rect
          x={node.x - 58}
          y={node.y - 26}
          width={116}
          height={52}
          fill="none"
          stroke="#10B981"
          strokeWidth={2}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.8, 0], scale: [1, 1.05, 1.1] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
        />
      )}

      {/* Node box */}
      <rect
        x={node.x - 50}
        y={node.y - 18}
        width={100}
        height={36}
        fill={active ? bg : "#0D0F14"}
        stroke={active ? color : "#1A2332"}
        strokeWidth={active ? 1.5 : 1}
      />

      {/* Label */}
      <text
        x={node.x}
        y={node.y - 2}
        textAnchor="middle"
        fill={active ? "#fff" : "#94A3B8"}
        fontSize={10}
        fontFamily="'JetBrains Mono', monospace"
        fontWeight={active ? 600 : 400}
      >
        {node.label}
      </text>

      {/* Classification badge */}
      {node.classification && (
        <g>
          <rect
            x={node.x - 26}
            y={node.y + 8}
            width={52}
            height={14}
            fill={classificationColor(node.classification) + "18"}
            stroke={classificationColor(node.classification)}
            strokeWidth={0.5}
          />
          <text
            x={node.x}
            y={node.y + 17}
            textAnchor="middle"
            fill={classificationColor(node.classification)}
            fontSize={7}
            fontFamily="'JetBrains Mono', monospace"
            fontWeight={700}
          >
            {node.classification}
          </text>
        </g>
      )}
    </motion.g>
  );
}

function GraphEdge({ edge, fromNode, toNode, active, taintColor, isBlocked, isAllowed }) {
  const dx = toNode.x - fromNode.x;
  const dy = toNode.y - fromNode.y;
  // offset to not overlap node boxes
  const len = Math.sqrt(dx * dx + dy * dy);
  const ox = (dx / len) * 52;
  const oy = (dy / len) * 20;
  const x1 = fromNode.x + ox;
  const y1 = fromNode.y + oy;
  const x2 = toNode.x - ox;
  const y2 = toNode.y - oy;

  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;

  let color = active ? "#475569" : "#1A2332";
  if (active && taintColor) color = taintColor;
  if (isBlocked) color = "#EF4444";
  if (isAllowed) color = "#10B981";

  return (
    <g>
      {/* Edge line */}
      <motion.line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={color}
        strokeWidth={active ? 2 : 1}
        strokeDasharray={isBlocked ? "6 4" : "none"}
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: active ? 1 : 0.3 }}
        transition={{ duration: 0.5 }}
      />

      {/* Arrow */}
      <motion.polygon
        points={`${x2},${y2} ${x2 - 6 * (dx / len) + 4 * (dy / len)},${
          y2 - 6 * (dy / len) - 4 * (dx / len)
        } ${x2 - 6 * (dx / len) - 4 * (dy / len)},${
          y2 - 6 * (dy / len) + 4 * (dx / len)
        }`}
        fill={color}
        initial={{ opacity: 0 }}
        animate={{ opacity: active ? 1 : 0.3 }}
      />

      {/* Animated data packet on active edges */}
      {active && taintColor && (
        <motion.circle
          r={3}
          fill={taintColor}
          filter={`drop-shadow(0 0 4px ${taintColor})`}
          initial={{ cx: x1, cy: y1, opacity: 0 }}
          animate={{
            cx: [x1, x2],
            cy: [y1, y2],
            opacity: [0, 1, 1, 0],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      )}

      {/* Blocked X */}
      {isBlocked && (
        <motion.g
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
        >
          <rect
            x={midX - 10}
            y={midY - 10}
            width={20}
            height={20}
            fill="#050508"
            stroke="#EF4444"
            strokeWidth={1.5}
          />
          <line x1={midX - 4} y1={midY - 4} x2={midX + 4} y2={midY + 4} stroke="#EF4444" strokeWidth={2} />
          <line x1={midX + 4} y1={midY - 4} x2={midX - 4} y2={midY + 4} stroke="#EF4444" strokeWidth={2} />
        </motion.g>
      )}

      {/* Allowed check */}
      {isAllowed && (
        <motion.g
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
        >
          <rect
            x={midX - 10}
            y={midY - 10}
            width={20}
            height={20}
            fill="#050508"
            stroke="#10B981"
            strokeWidth={1.5}
          />
          <polyline
            points={`${midX - 4},${midY} ${midX - 1},${midY + 3} ${midX + 5},${midY - 3}`}
            fill="none"
            stroke="#10B981"
            strokeWidth={2}
          />
        </motion.g>
      )}

      {/* Edge label */}
      {active && (
        <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
          <rect
            x={midX - 32}
            y={midY - 20}
            width={64}
            height={14}
            fill="#050508"
            stroke={color}
            strokeWidth={0.5}
            opacity={0.9}
          />
          <text
            x={midX}
            y={midY - 11}
            textAnchor="middle"
            fill={color}
            fontSize={7}
            fontFamily="'JetBrains Mono', monospace"
            fontWeight={500}
          >
            {edge.label}
          </text>
        </motion.g>
      )}
    </g>
  );
}

/* ─── Stat Card ─── */

function ImpactCard({ impact, color }) {
  const Icon = impact.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6 }}
      className="border p-4 flex items-start gap-3"
      style={{ borderColor: color + "40", backgroundColor: color + "08" }}
    >
      <div
        className="w-10 h-10 flex items-center justify-center border shrink-0"
        style={{ borderColor: color, color }}
      >
        <Icon size={18} />
      </div>
      <div>
        <span className="text-[10px] font-mono uppercase tracking-[0.2em] block mb-1" style={{ color: "#475569" }}>
          {impact.label}
        </span>
        <span className="text-2xl font-heading font-bold block" style={{ color }}>
          {impact.value}
        </span>
        <span className="text-[10px] text-[#475569]">{impact.sub}</span>
      </div>
    </motion.div>
  );
}

/* ─── Legend ─── */

function GraphLegend() {
  const items = [
    { color: "#00E5FF", label: "Data Source" },
    { color: "#F59E0B", label: "Processing" },
    { color: "#10B981", label: "Internal Target" },
    { color: "#EF4444", label: "External Target" },
    { color: "#94A3B8", label: "Temp Storage" },
  ];
  return (
    <div className="flex flex-wrap gap-4 mt-4">
      {items.map((item) => (
        <div key={item.label} className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5" style={{ backgroundColor: item.color }} />
          <span className="text-[10px] font-mono text-[#475569]">{item.label}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── Main Component ─── */

export default function TaintGraph() {
  const [activeScenario, setActiveScenario] = useState(0);
  const [step, setStep] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [showPlain, setShowPlain] = useState(false);
  const timerRef = useRef(null);

  const scenario = scenarios[activeScenario];
  const maxStep = Math.max(...scenario.edges.map((e) => e.step));

  const play = useCallback(() => {
    setStep(-1);
    setPlaying(true);
    let current = -1;
    timerRef.current = setInterval(() => {
      current++;
      if (current > maxStep + 1) {
        clearInterval(timerRef.current);
        setPlaying(false);
        return;
      }
      setStep(current);
    }, 1200);
  }, [maxStep]);

  useEffect(() => {
    return () => clearInterval(timerRef.current);
  }, []);

  const selectScenario = (i) => {
    clearInterval(timerRef.current);
    setActiveScenario(i);
    setStep(-1);
    setPlaying(false);
  };

  // Determine active/tainted state for each node and edge at current step
  const getEdgeState = (edge) => {
    const active = edge.step <= step;
    const isLeapforce = scenario.id === "leapforce";
    const isFalseNeg = scenario.id === "false-negative";
    const isFalsePos = scenario.id === "false-positive";
    const isFinished = step > maxStep;

    let taintColor = null;
    let isBlocked = false;
    let isAllowed = false;

    if (active) {
      if (isLeapforce) {
        if (edge.taintLevel === "SECRET") taintColor = "#EF4444";
        else if (edge.taintLevel === "CONFIDENTIAL") taintColor = "#F59E0B";
        else taintColor = "#10B981";
        if (edge.blocked && isFinished) isBlocked = true;
        if (edge.allowed && isFinished) isAllowed = true;
      } else if (isFalseNeg) {
        if (scenario.taintPath.includes(edge.step)) taintColor = "#EF4444";
        else taintColor = "#10B981";
      } else if (isFalsePos) {
        taintColor = "#10B981";
        if (edge.falseBlock && isFinished) isBlocked = true;
      }
    }

    return { active, taintColor, isBlocked, isAllowed };
  };

  const getNodeState = (node) => {
    // A node is active if any of its incoming edges have been shown
    const incoming = scenario.edges.filter((e) => e.to === node.id && e.step <= step);
    const outgoing = scenario.edges.filter((e) => e.from === node.id && e.step <= step);
    const active = node.id === "agent" ? step >= 0 : incoming.length > 0 || outgoing.length > 0;

    const isFinished = step > maxStep;
    const isLeapforce = scenario.id === "leapforce";
    const isFalsePos = scenario.id === "false-positive";

    let tainted = false;
    let taintColor = null;
    let pulseDanger = false;
    let pulseAllow = false;

    if (active) {
      if (isLeapforce) {
        // Find highest taint incoming
        const incomingEdges = scenario.edges.filter((e) => e.to === node.id && e.step <= step);
        const hasSecret = incomingEdges.some((e) => e.taintLevel === "SECRET");
        if (hasSecret || node.classification === "SECRET") {
          tainted = true;
          taintColor = "#EF4444";
        } else if (node.classification === "INTERNAL" || incomingEdges.some((e) => e.taintLevel === "INTERNAL")) {
          tainted = true;
          taintColor = "#10B981";
        }
        if (node.type === "external" && isFinished) pulseDanger = true;
        if (node.type === "internal" && isFinished && incomingEdges.length > 0) pulseAllow = true;
      } else if (scenario.id === "false-negative") {
        const inEdges = scenario.edges.filter((e) => e.to === node.id && e.step <= step);
        const hasTaint = inEdges.some((e) => scenario.taintPath.includes(e.step));
        if (hasTaint || node.classification === "SECRET") {
          tainted = true;
          taintColor = "#EF4444";
        }
        if (node.type === "external" && isFinished && hasTaint) pulseDanger = true;
      } else if (isFalsePos) {
        tainted = true;
        taintColor = "#10B981";
        if (isFinished && (node.type === "internal")) {
          const inEdges = scenario.edges.filter((e) => e.to === node.id);
          if (inEdges.some((e) => e.falseBlock)) pulseDanger = true;
        }
      }
    }

    return { active, tainted, taintColor, pulseDanger, pulseAllow };
  };

  const isFinished = step > maxStep;

  return (
    <section className="relative py-24 lg:py-32 bg-[#050508]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-6"
        >
          <span className="text-xs font-mono text-[#00E5FF] uppercase tracking-[0.2em] flex items-center gap-2 mb-4">
            <Network size={14} />
            Why Taint Tracking Matters
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-white mb-4">
            The <span className="text-[#00E5FF]">Hidden Complexity</span> of Agent Security
          </h2>
          <p className="text-base text-[#94A3B8] max-w-3xl leading-relaxed">
            Every AI agent call creates a web of data flows. A single piece of sensitive information can hop through
            multiple services before reaching an exit point. Getting this wrong in either direction is catastrophic.
          </p>
        </motion.div>

        {/* Plain language toggle */}
        <div className="mb-8">
          <button
            onClick={() => setShowPlain(!showPlain)}
            className="text-xs font-mono px-3 py-1.5 border transition-all flex items-center gap-2"
            style={{
              borderColor: showPlain ? "#00E5FF" : "#1A2332",
              color: showPlain ? "#00E5FF" : "#94A3B8",
              backgroundColor: showPlain ? "rgba(0, 229, 255, 0.05)" : "transparent",
            }}
          >
            <Users size={12} />
            {showPlain ? "Showing plain English" : "Show plain English explanation"}
          </button>
        </div>

        {/* Scenario tabs */}
        <div className="flex flex-wrap gap-0 mb-0 border-b border-[#1A2332]">
          {scenarios.map((s, i) => {
            const Icon = s.tabIcon;
            return (
              <button
                key={s.id}
                onClick={() => selectScenario(i)}
                className="px-5 py-3 text-xs font-mono uppercase tracking-[0.15em] flex items-center gap-2 transition-all border-b-2"
                style={{
                  color: activeScenario === i ? s.tabColor : "#475569",
                  borderBottomColor: activeScenario === i ? s.tabColor : "transparent",
                  backgroundColor: activeScenario === i ? s.tabColor + "08" : "transparent",
                }}
              >
                <Icon size={14} />
                {s.tab}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={scenario.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="border border-[#1A2332] border-t-0"
          >
            {/* Top bar */}
            <div className="flex items-center justify-between px-6 py-4 bg-[#0D0F14] border-b border-[#1A2332]">
              <div>
                <h3 className="text-lg font-semibold text-white mb-1">{scenario.headline}</h3>
                <p className="text-xs text-[#94A3B8] font-mono">{scenario.headlinePlain}</p>
              </div>
              <button
                onClick={play}
                disabled={playing}
                className="px-4 py-2 text-[10px] font-mono uppercase tracking-wider flex items-center gap-2 transition-all"
                style={{
                  backgroundColor: playing ? "#1A2332" : scenario.tabColor,
                  color: playing ? "#475569" : "#050508",
                  cursor: playing ? "not-allowed" : "pointer",
                }}
              >
                {playing ? (
                  "Tracing..."
                ) : (
                  <>
                    <Zap size={12} /> Trace Data Flow
                  </>
                )}
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12">
              {/* Graph area */}
              <div className="lg:col-span-8 p-6 bg-[#050508] min-h-[320px]">
                <svg
                  viewBox="0 0 620 170"
                  className="w-full h-auto"
                  style={{ maxHeight: "300px" }}
                >
                  {/* Grid dots for ambiance */}
                  {Array.from({ length: 11 }).map((_, ix) =>
                    Array.from({ length: 4 }).map((_, iy) => (
                      <circle
                        key={`${ix}-${iy}`}
                        cx={ix * 62}
                        cy={iy * 56 + 2}
                        r={0.6}
                        fill="#1A2332"
                      />
                    ))
                  )}

                  {/* Edges */}
                  {scenario.edges.map((edge, i) => {
                    const fromNode = scenario.nodes.find((n) => n.id === edge.from);
                    const toNode = scenario.nodes.find((n) => n.id === edge.to);
                    const state = getEdgeState(edge);
                    return (
                      <GraphEdge
                        key={i}
                        edge={edge}
                        fromNode={fromNode}
                        toNode={toNode}
                        {...state}
                      />
                    );
                  })}

                  {/* Nodes */}
                  {scenario.nodes.map((node) => {
                    const state = getNodeState(node);
                    return <GraphNode key={node.id} node={node} {...state} />;
                  })}
                </svg>

                <GraphLegend />
              </div>

              {/* Sidebar info */}
              <div className="lg:col-span-4 p-6 bg-[#0D0F14] border-l border-[#1A2332] flex flex-col justify-between">
                <div>
                  {/* Description */}
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={showPlain ? "plain" : "tech"}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="text-sm leading-relaxed mb-6"
                      style={{ color: "#94A3B8" }}
                    >
                      {showPlain ? scenario.plainDescription : scenario.description}
                    </motion.p>
                  </AnimatePresence>

                  {/* Step indicator */}
                  <div className="space-y-2 mb-6">
                    <span className="text-[10px] font-mono text-[#475569] uppercase tracking-[0.2em]">
                      Data Flow Steps
                    </span>
                    {scenario.edges.map((edge, i) => {
                      const isActive = edge.step <= step;
                      const edgeState = getEdgeState(edge);
                      return (
                        <motion.div
                          key={i}
                          className="flex items-center gap-2 py-1"
                          animate={{ opacity: isActive ? 1 : 0.3 }}
                        >
                          <div
                            className="w-4 h-4 flex items-center justify-center border text-[8px] font-mono"
                            style={{
                              borderColor: isActive ? (edgeState.taintColor || "#475569") : "#1A2332",
                              color: isActive ? (edgeState.taintColor || "#94A3B8") : "#1A2332",
                            }}
                          >
                            {i + 1}
                          </div>
                          <span
                            className="text-[10px] font-mono flex-1"
                            style={{ color: isActive ? "#94A3B8" : "#1A2332" }}
                          >
                            {edge.label}
                          </span>
                          {edgeState.isBlocked && (
                            <span className="text-[8px] font-mono text-[#EF4444] border border-[#EF4444] px-1">
                              BLOCKED
                            </span>
                          )}
                          {edgeState.isAllowed && (
                            <span className="text-[8px] font-mono text-[#10B981] border border-[#10B981] px-1">
                              OK
                            </span>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                </div>

                {/* Verdict + Impact */}
                <div>
                  {isFinished && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      {/* Verdict */}
                      <div
                        className="p-3 border mb-4 flex items-start gap-3"
                        style={{
                          borderColor: scenario.verdictColor + "60",
                          backgroundColor: scenario.verdictColor + "08",
                        }}
                      >
                        {scenario.id === "false-negative" && <ShieldOff size={16} className="text-[#EF4444] shrink-0 mt-0.5" />}
                        {scenario.id === "false-positive" && <Ban size={16} className="text-[#F59E0B] shrink-0 mt-0.5" />}
                        {scenario.id === "leapforce" && <ShieldCheck size={16} className="text-[#00E5FF] shrink-0 mt-0.5" />}
                        <p className="text-xs leading-relaxed" style={{ color: scenario.verdictColor }}>
                          {scenario.verdictMessage}
                        </p>
                      </div>

                      <ImpactCard impact={scenario.impact} color={scenario.verdictColor} />
                    </motion.div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Bottom summary cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-0 mt-0 border border-[#1A2332] border-t-0"
        >
          {[
            {
              icon: ShieldOff,
              color: "#EF4444",
              title: "False Negative",
              plainTitle: "Threat Slips Through",
              desc: "Sensitive data exfiltrated because the system couldn't trace information flow across service hops.",
              plainDesc: "Like a security guard who checks IDs at the door but doesn't notice someone carrying out confidential files in their backpack.",
            },
            {
              icon: Ban,
              color: "#F59E0B",
              title: "False Positive",
              plainTitle: "Legitimate Work Blocked",
              desc: "Authorized workflows blocked by overly broad rules that can't distinguish sensitivity levels.",
              plainDesc: "Like a fire alarm that goes off every time someone makes toast — eventually, people just ignore it.",
            },
            {
              icon: ShieldCheck,
              color: "#00E5FF",
              title: "Taint Tracking",
              plainTitle: "The Right Answer",
              desc: "Sensitivity labels follow data through every hop. Only actual policy violations trigger enforcement.",
              plainDesc: "Like invisible ink that only shows up when someone tries to take a document outside the building — no false alarms, no missed threats.",
            },
          ].map((card, i) => (
            <div
              key={card.title}
              className="p-6 bg-[#0D0F14] border-r border-[#1A2332] last:border-r-0"
            >
              <div className="flex items-center gap-2 mb-3">
                <card.icon size={16} style={{ color: card.color }} />
                <span className="text-xs font-mono font-bold uppercase tracking-[0.15em]" style={{ color: card.color }}>
                  {showPlain ? card.plainTitle : card.title}
                </span>
              </div>
              <p className="text-sm text-[#94A3B8] leading-relaxed">
                {showPlain ? card.plainDesc : card.desc}
              </p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
